import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  PurchaseOrderItemAllocation,
  CostCenterType,
  MockProject,
  MockWorkCategory
} from './PurchaseOrderFormData';
import { Plus, Trash2, Building2, Briefcase } from 'lucide-react';

interface ItemAllocationsEditorProps {
  allocations: PurchaseOrderItemAllocation[];
  unitPrice: number;
  unit: string;
  currency: string;
  onChange: (allocations: PurchaseOrderItemAllocation[]) => void;
  projects: MockProject[];
  generalWorkCategories: MockWorkCategory[];
}

export const ItemAllocationsEditor: React.FC<ItemAllocationsEditorProps> = ({
  allocations,
  unitPrice,
  unit,
  currency,
  onChange,
  projects,
  generalWorkCategories
}) => {
  const addAllocation = () => {
    const newAllocation: PurchaseOrderItemAllocation = {
      costCenterType: 'PROJECT',
      projectId: null,
      generalCostCenterId: null,
      workCategoryId: 0,
      allocatedQuantity: 0,
    };
    onChange([...allocations, newAllocation]);
  };

  const removeAllocation = (index: number) => {
    onChange(allocations.filter((_, i) => i !== index));
  };

  const updateAllocation = (index: number, updates: Partial<PurchaseOrderItemAllocation>) => {
    onChange(allocations.map((alloc, i) => i === index ? { ...alloc, ...updates } : alloc));
  };

  const handleTypeChange = (index: number, costCenterType: CostCenterType) => {
    updateAllocation(index, {
      costCenterType,
      projectId: null,
      generalCostCenterId: null,
      workCategoryId: 0,
    });
  };

  const getAvailableProjects = (currentIndex: number): MockProject[] => {
    const selectedProjectIds = allocations
      .map((alloc, idx) => idx !== currentIndex && alloc.costCenterType === 'PROJECT' ? alloc.projectId : null)
      .filter((id): id is number => id !== null);
    return projects.filter(project => !selectedProjectIds.includes(project.id));
  };

  const getAvailableGeneralCategories = (currentIndex: number): MockWorkCategory[] => {
    const selectedGeneralCategoryIds = allocations
      .map((alloc, idx) => idx !== currentIndex && alloc.costCenterType === 'GENERAL' ? alloc.workCategoryId : null)
      .filter((id): id is number => id !== null && id !== 0);
    return generalWorkCategories.filter(cat =>
      !cat.isSystemDefault && !selectedGeneralCategoryIds.includes(cat.id)
    );
  };

  const getAvailableWorkCategories = (allocation: PurchaseOrderItemAllocation) => {
    if (allocation.costCenterType === 'PROJECT' && allocation.projectId) {
      const project = projects.find(p => p.id === allocation.projectId);
      return project?.workCategories || [];
    }
    return generalWorkCategories.filter(cat => !cat.isSystemDefault);
  };

  const totalAllocated = allocations.reduce((sum, alloc) => sum + alloc.allocatedQuantity, 0);

  const getAvailableCostCentersCount = (): number => {
    const usedProjectIds = allocations
      .filter(alloc => alloc.costCenterType === 'PROJECT')
      .map(alloc => alloc.projectId)
      .filter((id): id is number => id !== null);

    const usedGeneralCategoryIds = allocations
      .filter(alloc => alloc.costCenterType === 'GENERAL')
      .map(alloc => alloc.workCategoryId)
      .filter((id): id is number => id !== 0);

    const availableProjectsCount = projects.filter(p => !usedProjectIds.includes(p.id)).length;
    const availableGeneralCategoriesCount = generalWorkCategories.filter(
      cat => !cat.isSystemDefault && !usedGeneralCategoryIds.includes(cat.id)
    ).length;

    return availableProjectsCount + availableGeneralCategoriesCount;
  };

  const canAddMoreAllocations = getAvailableCostCentersCount() > 0;

  return (
    <div className="p-4 space-y-4 border-2 rounded-lg border-primary/20 bg-primary/5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Distribución de Cantidades</h3>
        <Button
          onClick={addAllocation}
          size="sm"
          className="rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          disabled={!canAddMoreAllocations}
          title={!canAddMoreAllocations ? 'No hay más centros de costo disponibles' : 'Agregar nueva asignación'}
        >
          <Plus className="w-3 h-3 mr-1" />
          Agregar
        </Button>
      </div>

      {allocations.length === 0 ? (
        <div className="py-6 text-center border border-dashed rounded-lg border-primary/20 bg-card/50">
          <p className="mb-2 text-xs font-medium text-muted-foreground">No hay asignaciones</p>
          <p className="mb-3 text-xs text-muted-foreground/70">Agrega al menos una asignación para este item</p>
          <Button
            onClick={addAllocation}
            size="sm"
            variant="outline"
            className="rounded-lg"
          >
            <Plus className="w-3 h-3 mr-1" />
            Agregar Asignación
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {allocations.map((allocation, index) => {
            const availableCategories = getAvailableWorkCategories(allocation);
            const availableProjects = getAvailableProjects(index);
            const availableGeneralCategories = getAvailableGeneralCategories(index);
            const amount = allocation.allocatedQuantity * unitPrice;

            return (
              <div key={index} className="p-3 bg-card border rounded-lg border-border">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-primary text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="text-xs font-semibold text-foreground">Asignación {index + 1}</span>
                  <Button
                    onClick={() => removeAllocation(index)}
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {/* Tipo */}
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs font-medium text-foreground">
                      Tipo <span className="text-xs text-red-500">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <label
                        className={`flex items-center flex-1 p-2 text-xs transition-all border rounded-lg cursor-pointer hover:bg-accent ${
                          allocation.costCenterType === 'PROJECT' ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={allocation.costCenterType === 'PROJECT'}
                          onChange={() => handleTypeChange(index, 'PROJECT')}
                          className="w-3 h-3 mr-2"
                        />
                        <Building2 className="w-3 h-3 mr-1 text-primary" />
                        <span className="text-foreground">Proyecto</span>
                      </label>
                      <label
                        className={`flex items-center flex-1 p-2 text-xs transition-all border rounded-lg cursor-pointer hover:bg-accent ${
                          allocation.costCenterType === 'GENERAL' ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={allocation.costCenterType === 'GENERAL'}
                          onChange={() => handleTypeChange(index, 'GENERAL')}
                          className="w-3 h-3 mr-2"
                        />
                        <Briefcase className="w-3 h-3 mr-1 text-primary" />
                        <span className="text-foreground">Rubro General</span>
                      </label>
                    </div>
                  </div>

                  {/* Proyecto */}
                  {allocation.costCenterType === 'PROJECT' ? (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-foreground">
                        Obra <span className="text-xs text-red-500">*</span>
                      </Label>
                      <Select
                        value={allocation.projectId ? String(allocation.projectId) : ''}
                        onValueChange={(value) => updateAllocation(index, {
                          projectId: Number(value),
                          workCategoryId: 0
                        })}
                      >
                        <SelectTrigger className="text-xs rounded-lg bg-background border-border">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent sortAlphabetically>
                          {availableProjects.map((project) => (
                            <SelectItem key={project.id} value={String(project.id)}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {availableProjects.length === 0 && (
                        <p className="text-xs text-warning">Todos los proyectos ya están asignados</p>
                      )}
                    </div>
                  ) : null}

                  {/* Rubro */}
                  {((allocation.costCenterType === 'PROJECT' && allocation.projectId) ||
                    allocation.costCenterType === 'GENERAL') && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-foreground">
                        Rubro <span className="text-xs text-red-500">*</span>
                      </Label>
                      <Select
                        value={allocation.workCategoryId ? String(allocation.workCategoryId) : ''}
                        onValueChange={(value) => updateAllocation(index, { workCategoryId: Number(value) })}
                      >
                        <SelectTrigger className="text-xs rounded-lg bg-background border-border">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent sortAlphabetically>
                          {(allocation.costCenterType === 'GENERAL' ? availableGeneralCategories : availableCategories).map((category) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {allocation.costCenterType === 'GENERAL' && availableGeneralCategories.length === 0 && (
                        <p className="text-xs text-warning">Todos los rubros generales ya están asignados</p>
                      )}
                    </div>
                  )}

                  {/* Cantidad */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">
                      Cantidad ({unit}) <span className="text-xs text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={allocation.allocatedQuantity || ''}
                      onChange={(e) => updateAllocation(index, { allocatedQuantity: Number(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="text-xs rounded-lg bg-background border-border focus-visible:ring-primary"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Importe */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">Importe</Label>
                    <Input
                      type="text"
                      value={`${currency} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      readOnly
                      className="text-xs font-semibold rounded-lg bg-primary/5 border-primary/20 text-primary"
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Total Asignado */}
          <div className="flex items-center justify-between p-3 border rounded-lg border-border bg-card">
            <span className="text-sm font-semibold text-foreground">Total Asignado:</span>
            <span className={`text-sm font-bold ${totalAllocated > 0 ? 'text-primary' : 'text-muted-foreground'}`}>
              {totalAllocated.toFixed(2)} {unit}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
